import type { EntitlementResult } from '@/hooks/useEntitlement';
import {
  inferLifecyclePhase,
  type LifecyclePhase,
  type SubscriptionState,
} from '@/hooks/useSubscriptionState';
import type { PlanStatusCardProps } from '@/components/patterns/PlanStatusCard';
import { pickReserveLine } from '@/features/subscription/pick-reserve-line';
import { STRINGS } from '@/strings/strings';

/**
 * Options object for `derivePlanStatusProps`. The two
 * `subscription*` fields are optional — required only when the
 * entitlement carries a `subscription` source (Batch 10 Sub-phase B —
 * Decision #16 precedence rule). Existing callers that don't pass
 * them continue to work; the function falls back to a generic "Abo"
 * headline + pack-style count and emits a dev-time `console.warn`.
 *
 * Sub-phase F wires both fields from each consumer's existing
 * `useSubscriptionState()` hook.
 */
export interface DerivePlanStatusOptions {
  chooseplanTarget: '/account' | '/account/subscription';
  /** Tier label from `useSubscriptionState().tierLabel` (e.g., "Entdecker"). */
  subscriptionTierLabel?: string | null;
  /** `storiesPerMonth` from `useSubscriptionState()` — drives the "X von Y" subtitle. */
  subscriptionStoriesPerMonth?: number | null;
  /**
   * Trigger fired by the "Geschichte erstellen" CTA (totalRemaining > 0
   * branch). Sub-phase D wires this to `useWorldSelectionSheet().openSheet`
   * in StartRoute + AccountRoute, replacing the prior `navigate('/builder')`
   * call (the bare /builder route was removed per Decision #13 Path A).
   *
   * Optional for backwards-compat: when omitted, the function falls back
   * to `navigate('/builder')` and emits a dev-time `console.warn` because
   * the route is gone. The fallback path will be removed once all 4
   * production consumers (StartRoute, AccountRoute, StoryFooterActions,
   * LibraryRoute) are wired through Sub-phase D + F.
   */
  onCreateStoryClick?: () => void;
}

/**
 * Map a raw `EntitlementResult` to PlanStatusCard props for the
 * Credits-states surface.
 *
 * Sebastian Gate-1 verbatim lock 2026-04-27 — Decision #16 precedence
 * rule (Subscription > Pack > Trial). The discriminator walks
 * `entitlement.sources` by `type`, **not** by array order — first
 * present source-type wins regardless of how the backend ordered
 * them. Closes PWA Open Items #49 (state-aware label discrimination).
 *
 * CTA logic remains decoupled from the precedence rule:
 *   - `totalRemaining === 0` → "Plan wählen" → `options.chooseplanTarget`
 *   - `totalRemaining > 0`   → "Geschichte erstellen" → `/builder`
 *
 * (`/builder` will be replaced with the WorldSelectionSheet trigger
 * in Sub-phase D — Decision #13.)
 *
 * Per Batch 9A FB-4: Start-Page → /account, Account-Page A-01 →
 * /account/subscription. Caller passes its own navigate target via
 * `chooseplanTarget`.
 */
export function derivePlanStatusProps(
  entitlement: EntitlementResult | null,
  navigate: (path: string) => void,
  options: DerivePlanStatusOptions = { chooseplanTarget: '/account' },
): PlanStatusCardProps {
  const S = STRINGS.startPage.planStatus;
  const isEmpty = !entitlement || entitlement.totalRemaining === 0;
  const ctaLabel = isEmpty ? S.ctaChoosePlan : S.ctaCreate;

  let onCtaClick: () => void;
  if (isEmpty) {
    onCtaClick = () => navigate(options.chooseplanTarget);
  } else if (options.onCreateStoryClick) {
    onCtaClick = options.onCreateStoryClick;
  } else {
    // Sub-phase F cleanup — fallback path removed per Decision #13
    // closure. Bare /builder route was deleted in Sub-phase D; all 4
    // production consumers (StartRoute, AccountRoute,
    // StoryFooterActions, LibraryRoute) wire `onCreateStoryClick` from
    // `useWorldSelectionSheet().openSheet`. Throwing on the
    // Geschichte-erstellen branch when the callback is missing catches
    // misuse loudly (matches the WorldSelectionSheetContext hook-throw
    // pattern from Sub-phase D2).
    throw new Error(
      '[derivePlanStatusProps] Geschichte-erstellen branch reached without ' +
        '`onCreateStoryClick` in options. Wire `useWorldSelectionSheet().openSheet` ' +
        'from the calling route.',
    );
  }

  const label = pickLabel(entitlement, options);
  return { ...label, ctaLabel, onCtaClick };
}

type LabelPair = Pick<PlanStatusCardProps, 'headline' | 'subtitleText' | 'reserveLineText'>;

function pickLabel(
  entitlement: EntitlementResult | null,
  options: DerivePlanStatusOptions,
): LabelPair {
  const S = STRINGS.startPage.planStatus;

  // Null entitlement (pre-fetch race window) → trial-empty default.
  if (!entitlement) {
    return { headline: S.headlineTrial, subtitleText: S.subtitleZeroOfOne };
  }

  // Decision #16 precedence: Subscription > Pack > Trial.
  const hasSubscription = entitlement.sources.some((s) => s.type === 'subscription');
  const hasPack = entitlement.sources.some((s) => s.type === 'pack');
  const hasTrial = entitlement.sources.some((s) => s.type === 'trial');

  if (hasSubscription) return subscriptionLabel(entitlement, options);
  if (hasPack) {
    return {
      headline: S.headlinePack,
      subtitleText: formatPackSubtitle(entitlement.packRemaining),
    };
  }
  if (hasTrial) {
    return {
      headline: S.headlineTrial,
      subtitleText: formatTrialSubtitle(entitlement.totalRemaining),
    };
  }
  // sources=[] (no entitlement at all) → trial-empty fallback.
  return { headline: S.headlineTrial, subtitleText: S.subtitleZeroOfOne };
}

function subscriptionLabel(
  entitlement: EntitlementResult,
  options: DerivePlanStatusOptions,
): LabelPair {
  const S = STRINGS.startPage.planStatus;
  const tierLabel = options.subscriptionTierLabel ?? null;
  const storiesPerMonth = options.subscriptionStoriesPerMonth ?? null;

  let headline: string;
  if (tierLabel) {
    headline = S.headlineActiveTemplate.replace('{tierLabel}', tierLabel);
  } else {
    // Defensive fallback — Sub-phase F wires the field from
    // useSubscriptionState() in StartRoute + AccountRoute.
    console.warn(
      '[derivePlanStatusProps] subscription source detected but `subscriptionTierLabel` not provided — falling back to "Abo" headline. Wire `useSubscriptionState().tierLabel` in the caller (Sub-phase F).',
    );
    headline = 'Abo';
  }

  let subtitleText: string;
  if (storiesPerMonth !== null && storiesPerMonth > 0) {
    // Subscription-Area Polish workstream 2026-04-30 — feed remaining
    // directly (no `used` computation) and let the new STRINGS template
    // surface "Noch X von Y verfügbar". Cleaner anyway: remaining is
    // already on the entitlement, no need to derive from storiesPerMonth.
    const remaining = Math.max(0, entitlement.subscriptionRemaining);
    subtitleText = S.subtitleSubscriptionUsage
      .replace('{remaining}', String(remaining))
      .replace('{total}', String(storiesPerMonth));
  } else {
    console.warn(
      '[derivePlanStatusProps] subscription source detected but `subscriptionStoriesPerMonth` not provided — falling back to pack-style count. Wire `useSubscriptionState().storiesPerMonth` in the caller (Sub-phase F).',
    );
    subtitleText = formatPackSubtitle(entitlement.subscriptionRemaining);
  }

  // PWA #71 — mixed-state override. When the user's subscription is
  // exhausted (sub=0) but they have non-sub credits available (pack
  // or trial), pickReserveLine returns the depletion + reserve subline
  // pair. Replace the "Noch 0 von Y" subtitle with the depletion line
  // and surface the reserve as a second line; CTA stays "Jetzt
  // Geschichte erstellen" because totalRemaining > 0.
  const reserve = pickReserveLine(entitlement);
  if (reserve) {
    return {
      headline,
      subtitleText: reserve.depletionLine,
      reserveLineText: reserve.reserveLine,
    };
  }

  return { headline, subtitleText };
}

function formatTrialSubtitle(n: number): string {
  const S = STRINGS.startPage.planStatus;
  if (n === 0) return S.subtitleZeroOfOne; // Decision #17
  if (n === 1) return S.subtitleOneAvailable;
  return S.subtitleNAvailable.replace('{n}', String(n));
}

function formatPackSubtitle(n: number): string {
  const S = STRINGS.startPage.planStatus;
  if (n === 1) return S.subtitlePackAvailableOne;
  return S.subtitlePackAvailable.replace('{n}', String(n));
}

/**
 * Map a raw `SubscriptionState` to PlanStatusCard props when a
 * lifecycle event is active. Returns `null` when no lifecycle event
 * applies — caller falls through to `derivePlanStatusProps()` for the
 * credits-states rendering.
 *
 * Lifecycle priority is computed by `inferLifecyclePhase()`
 * (past_due > pending_cancel > pending_plan_change). Each branch maps
 * to a PlanStatusCard `tone`:
 *
 *   - past_due             → tone='error', "Zahlungsmethode aktualisieren"
 *                            CTA opens the Stripe Customer Portal flow
 *                            `payment_method_update`.
 *   - pending_cancel       → tone='warning', "Kündigung zurückziehen"
 *                            CTA calls the explicit /api/subscriptions/
 *                            reactivate endpoint via the route's
 *                            handler (not handled here — derivation
 *                            returns the action callback).
 *   - pending_plan_change  → tone='warning', "Plan-Wechsel rückgängig
 *                            machen" CTA opens the Stripe Customer
 *                            Portal flow `subscription_update`.
 *
 * The actions are passed in by the caller (route owns the side-effect
 * wiring), keeping this function pure + testable.
 *
 * Compact subtitle copy is used here (subtitle slot of PlanStatusCard
 * is a single string). The richer multi-line "highlight prefix + body"
 * shape used by the A-02 SubscriptionSummaryCard is rendered there
 * directly with full STRINGS.account.subscription.lifecycle.* — not
 * via PlanStatusCard.
 */
export interface LifecycleActionHandlers {
  /** Called when user taps the Past-Due "Zahlungsmethode aktualisieren" CTA. */
  onUpdatePaymentMethod: () => void;
  /** Called when user taps the Pending-Cancel "Kündigung zurückziehen" CTA. */
  onReactivate: () => void;
  /** Called when user taps the Pending-Plan-Change "Plan-Wechsel rückgängig machen" CTA. */
  onCancelPendingPlanChange: () => void;
}

export function deriveLifecyclePlanStatusProps(
  state: SubscriptionState | null,
  handlers: LifecycleActionHandlers,
): PlanStatusCardProps | null {
  const phase: LifecyclePhase = inferLifecyclePhase(state);
  if (!phase || !state) return null;

  const lc = STRINGS.account.subscription.lifecycle;

  if (phase === 'past_due') {
    return {
      headline: lc.pastDue.headlineCard,
      subtitleText: lc.pastDue.compactSubtitle,
      ctaLabel: lc.pastDue.ctaLabel,
      onCtaClick: handlers.onUpdatePaymentMethod,
      tone: 'error',
    };
  }

  if (phase === 'pending_cancel') {
    return {
      headline: replaceTierLabel(lc.pendingCancel.headlineCard, state.tierLabel),
      subtitleText: lc.pendingCancel.compactSubtitle.replace(
        '{date}',
        formatGermanShortDate(state.endsAt ?? state.currentPeriodEnd),
      ),
      ctaLabel: lc.pendingCancel.ctaLabel,
      onCtaClick: handlers.onReactivate,
      tone: 'warning',
    };
  }

  // phase === 'pending_plan_change'
  // Bug 2C: a scheduled plan change still leaves the current plan's
  // monthly quota active until the change takes effect. Surface the
  // "Noch X von Y verfügbar" usage line alongside the banner so Start +
  // Account match the Abo page (which shows both). Computed from the
  // current tier's quota fields — identical formula + STRINGS template
  // to SubscriptionSummaryCard's counter row.
  const remaining = Math.max(0, state.storiesPerMonth - state.storiesUsedThisMonth);
  const quotaLineText = STRINGS.startPage.planStatus.subtitleSubscriptionUsage
    .replace('{remaining}', String(remaining))
    .replace('{total}', String(state.storiesPerMonth));

  return {
    headline: replaceTierLabel(lc.pendingPlanChange.headlineCard, state.tierLabel),
    subtitleText: lc.pendingPlanChange.compactSubtitle
      .replace('{date}', formatGermanShortDate(state.pendingDowngradeAt))
      .replace('{newPlanName}', state.pendingDowngradeTierLabel ?? '—'),
    quotaLineText,
    ctaLabel: lc.pendingPlanChange.ctaLabel,
    onCtaClick: handlers.onCancelPendingPlanChange,
    tone: 'warning',
  };
}

// ── Local helpers ─────────────────────────────────────────────

function replaceTierLabel(template: string, tierLabel: string | null): string {
  return template.replace('{tierLabel}', tierLabel ?? '—');
}

function formatGermanShortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
}
